import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme, jadeGreen, garnetRed, goldenTopaz, blueGrey } from '../design-system/theme';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { formatDateTime } from '../utils/dateFormat';
import {
  PageHeader,
  PageTitle,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  IconButton,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CloseButton,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
  PrimaryButton,
  SecondaryButton,
  Textarea,
  Label,
} from '../styles/adminShared';

// Types
interface ErasureRequest {
  consentId: string;
  tenantId: string;
  dataOwnerId: string;
  status: string;
  withdrawnAt?: string;
  withdrawalReason?: string;
  expiresAt?: string;
  dataErasureRequired: boolean;
  dataErasureConfirmedAt?: string;
  dataErasureConfirmedBy?: string;
  dataErasureNotes?: string;
  templateId?: string;
  formId?: string;
  regionCode?: string;
}

interface ErasureStats {
  totalRequiringErasure: number;
  pendingConfirmation: number;
  confirmed: number;
  overdue: number;
}

// Styled Components
const TitleSection = styled.div`
  h1 {
    color: ${theme.textPrimary};
    margin: 0;
    font-size: 24px;
  }
  
  p {
    color: ${theme.textSecondary};
    margin: 8px 0 0 0;
    font-size: 14px;
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div<{ $color?: string }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid ${props => props.$color || theme.primary};
  
  .value {
    font-size: 28px;
    font-weight: 700;
    color: ${theme.textPrimary};
  }
  
  .label {
    font-size: 13px;
    color: ${theme.textSecondary};
    margin-top: 4px;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const ConfirmationForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  .label {
    font-size: 12px;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 14px;
    color: ${theme.textPrimary};
    font-weight: 500;
  }
`;

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: ${goldenTopaz[1]};
  border: 1px solid ${goldenTopaz[4]};
  border-radius: 8px;
  margin-bottom: 16px;

  .icon {
    font-size: 24px;
  }

  .text {
    font-size: 14px;
    color: ${goldenTopaz[8]};
  }
`;

const OverdueBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${garnetRed[1] || '#fef2f2'};
  color: ${garnetRed[7] || '#b91c1c'};
  border: 1px solid ${garnetRed[3] || '#fca5a5'};
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;

  .warning-icon {
    font-size: 14px;
    line-height: 1;
  }
`;

const DaysPendingNormal = styled.span`
  font-size: 13px;
  color: ${theme.textPrimary};
`;

export default function DataErasure() {
  const [requests, setRequests] = useState<ErasureRequest[]>([]);
  const [stats, setStats] = useState<ErasureStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modal state
  const [selectedRequest, setSelectedRequest] = useState<ErasureRequest | null>(null);
  const [confirmationNotes, setConfirmationNotes] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [pendingResponse, statsResponse] = await Promise.all([
        adminApi.getErasurePending(),
        adminApi.getErasureStats(),
      ]);
      setRequests(pendingResponse);
      setStats(statsResponse);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data erasure requests');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmErasure = async () => {
    if (!selectedRequest) return;
    
    setConfirming(true);
    try {
      await adminApi.confirmErasure(selectedRequest.consentId, {
        confirmedBy: 'admin', // TODO: Get from auth context
        notes: confirmationNotes,
      });
      
      // Refresh data
      await loadData();
      setSelectedRequest(null);
      setConfirmationNotes('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to confirm erasure');
    } finally {
      setConfirming(false);
    }
  };

  const getDaysSince = (dateString?: string) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    return Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <AdminShell title="Data Erasure">
        <LoadingSpinner />
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Data Erasure">
      <PageHeader>
        <TitleSection>
          <PageTitle>Data Erasure Confirmation</PageTitle>
          <p>Confirm that personal data has been erased after consent expiry or withdrawal (GDPR Art. 17 / DPDP compliance)</p>
        </TitleSection>
      </PageHeader>

      {error && (
        <AlertMessage $variant="error" style={{ marginBottom: 16 }}>
          {error}
        </AlertMessage>
      )}

      {/* Stats */}
      {stats && (
        <StatsRow>
          <StatCard $color={goldenTopaz[7]}>
            <div className="value">{stats.pendingConfirmation}</div>
            <div className="label">Pending Confirmation</div>
          </StatCard>
          <StatCard $color={garnetRed[6]}>
            <div className="value">{stats.overdue}</div>
            <div className="label">Overdue (&gt;30 days)</div>
          </StatCard>
          <StatCard $color={jadeGreen[6]}>
            <div className="value">{stats.confirmed}</div>
            <div className="label">Confirmed</div>
          </StatCard>
          <StatCard $color={blueGrey[6]}>
            <div className="value">{stats.totalRequiringErasure}</div>
            <div className="label">Total Requiring Erasure</div>
          </StatCard>
        </StatsRow>
      )}

      {/* Pending Requests Table */}
      {requests.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateIcon>✅</EmptyStateIcon>
          <EmptyStateTitle>All Caught Up!</EmptyStateTitle>
          <EmptyStateText>
            No pending data erasure confirmations. All expired/withdrawn consents have been processed.
          </EmptyStateText>
        </EmptyStateContainer>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <Tr>
                <Th>Consent ID</Th>
                <Th>Data Owner</Th>
                <Th>Status</Th>
                <Th>Since</Th>
                <Th>Days Pending</Th>
                <Th>Region</Th>
                <Th>Actions</Th>
              </Tr>
            </thead>
            <tbody>
              {requests.map((request) => {
                const daysPending = getDaysSince(request.withdrawnAt || request.expiresAt);
                const isOverdue = daysPending > 30;
                
                return (
                  <Tr key={request.consentId}>
                    <Td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {request.consentId.substring(0, 12)}...
                    </Td>
                    <Td style={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {request.dataOwnerId?.substring(0, 12)}...
                    </Td>
                    <Td>
                      <StatusBadge $status={request.status}>
                        {request.status}
                      </StatusBadge>
                    </Td>
                    <Td>
                      {formatDateTime(request.withdrawnAt || request.expiresAt || '')}
                    </Td>
                    <Td>
                      {isOverdue ? (
                        <OverdueBadge>
                          <span className="warning-icon">⚠️</span>
                          Overdue — {daysPending} days
                        </OverdueBadge>
                      ) : (
                        <DaysPendingNormal>{daysPending} days</DaysPendingNormal>
                      )}
                    </Td>
                    <Td>{request.regionCode || '-'}</Td>
                    <Td>
                      <IconButton 
                        onClick={() => setSelectedRequest(request)}
                        title="Confirm Erasure"
                      >
                        ✓ Confirm
                      </IconButton>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {/* Confirmation Modal */}
      {selectedRequest && (
        <ModalOverlay $show={!!selectedRequest} onClick={() => setSelectedRequest(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <ModalHeader>
              <ModalTitle>Confirm Data Erasure</ModalTitle>
              <CloseButton onClick={() => setSelectedRequest(null)}>×</CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <WarningBanner>
                <span className="icon">⚠️</span>
                <span className="text">
                  By confirming, you certify that all personal data associated with this consent 
                  has been permanently deleted from all systems, backups, and third-party processors.
                </span>
              </WarningBanner>
              
              <DetailGrid>
                <DetailItem>
                  <div className="label">Consent ID</div>
                  <div className="value" style={{ fontFamily: 'monospace' }}>
                    {selectedRequest.consentId}
                  </div>
                </DetailItem>
                <DetailItem>
                  <div className="label">Data Owner ID</div>
                  <div className="value" style={{ fontFamily: 'monospace' }}>
                    {selectedRequest.dataOwnerId}
                  </div>
                </DetailItem>
                <DetailItem>
                  <div className="label">Status</div>
                  <div className="value">{selectedRequest.status}</div>
                </DetailItem>
                <DetailItem>
                  <div className="label">
                    {selectedRequest.status === 'WITHDRAWN' ? 'Withdrawn At' : 'Expired At'}
                  </div>
                  <div className="value">
                    {formatDateTime(selectedRequest.withdrawnAt || selectedRequest.expiresAt || '')}
                  </div>
                </DetailItem>
                {selectedRequest.withdrawalReason && (
                  <DetailItem style={{ gridColumn: '1 / -1' }}>
                    <div className="label">Withdrawal Reason</div>
                    <div className="value">{selectedRequest.withdrawalReason}</div>
                  </DetailItem>
                )}
              </DetailGrid>
              
              <ConfirmationForm style={{ marginTop: 24 }}>
                <div>
                  <Label htmlFor="erasure-notes">Erasure Notes (required)</Label>
                  <Textarea
                    id="erasure-notes"
                    value={confirmationNotes}
                    onChange={(e) => setConfirmationNotes(e.target.value)}
                    placeholder="Describe what data was deleted and from which systems (e.g., 'Deleted from CRM, marketing database, and all backup systems')"
                    rows={4}
                  />
                </div>
              </ConfirmationForm>
            </ModalBody>
            
            <ModalFooter>
              <SecondaryButton onClick={() => setSelectedRequest(null)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton 
                onClick={handleConfirmErasure}
                disabled={!confirmationNotes.trim() || confirming}
              >
                {confirming ? 'Confirming...' : '✓ Confirm Erasure Complete'}
              </PrimaryButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </AdminShell>
  );
}
